/**
 * packageSchedule.js
 * ------------------
 * Pure helper functions for rendering a Package's `class_slots[]` as
 * human-readable schedule strings on the public Coach landing page card.
 *
 * Phase 67 — Scott Metcalf feedback: "make visible the lesson time,
 * start and stop dates, and the days of the week to the signup display
 * page." Each slot is rendered as its own row so a package with two
 * 4 PM + 5 PM slots no longer collapses into a single misleading
 * "10 sessions" line.
 *
 * Conventions:
 *   - weekday integers follow the JS convention used everywhere in the
 *     editor: 0=Sun, 1=Mon, ..., 6=Sat
 *   - `time_local` is "HH:MM" (24h) — already in the coach's profile tz
 *   - dates are ISO "YYYY-MM-DD"
 */

import { fmtTime12hLong } from './api';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Format an array of weekday integers (JS convention) as a human string.
 *  [2]               -> "Tue"
 *  [2, 3]            -> "Tue & Wed"
 *  [1, 3, 5]         -> "Mon, Wed, Fri"
 *  [1, 2, 3, 4, 5]   -> "Weekdays"
 *  [0, 6]            -> "Weekends"
 *  [0,1,2,3,4,5,6]   -> "Daily"
 */
export function formatWeekdays(weekdays) {
    if (!Array.isArray(weekdays) || weekdays.length === 0) return '';
    // Normalize: unique + sorted ascending
    const days = [...new Set(weekdays.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort((a, b) => a - b);
    if (days.length === 0) return '';
    if (days.length === 7) return 'Daily';
    if (days.length === 5 && days.join(',') === '1,2,3,4,5') return 'Weekdays';
    if (days.length === 2 && days.join(',') === '0,6') return 'Weekends';
    const labels = days.map((d) => WEEKDAY_SHORT[d]);
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
    return labels.join(', ');
}

/**
 * Format an ISO "YYYY-MM-DD" date as "Aug 5" (no year) or "Aug 5, 2026"
 * when withYear is true. Returns "" if the input is not parseable.
 */
export function formatShortDate(iso, withYear = false) {
    if (!iso || typeof iso !== 'string' || iso.length < 10) return '';
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = MONTHS[m - 1] || '';
    if (!monthLabel) return '';
    return withYear ? `${monthLabel} ${d}, ${y}` : `${monthLabel} ${d}`;
}

/**
 * Resolve the effective date range for a single class slot, taking the
 * package-level start_date/end_date into account when the slot itself
 * doesn't specify a window. Returns { start, end } as ISO strings (or "").
 */
function resolveSlotRange(slot, pkg) {
    const start = (slot && slot.start_date) || (pkg && pkg.start_date) || '';
    const end = (slot && slot.end_date) || (pkg && pkg.end_date) || '';
    return { start, end };
}

/**
 * Format the date range portion of a schedule line. Hides the year when
 * both dates fall in the current calendar year (cleaner on cards).
 * Returns "" when no range is defined (open-ended package).
 */
export function formatDateRange(start, end) {
    if (!start && !end) return '';
    const thisYear = new Date().getFullYear();
    const startYear = start ? Number(start.slice(0, 4)) : null;
    const endYear = end ? Number(end.slice(0, 4)) : null;
    const showStartYear = startYear !== null && startYear !== thisYear;
    const showEndYear = endYear !== null && endYear !== thisYear;
    if (start && end) {
        const a = formatShortDate(start, showStartYear);
        const b = formatShortDate(end, showEndYear);
        return a && b ? `${a} \u2013 ${b}` : a || b;
    }
    if (start) return `Starts ${formatShortDate(start, showStartYear)}`;
    return `Through ${formatShortDate(end, showEndYear)}`;
}

/**
 * Count how many real (non-skipped) lesson occurrences a single weekly
 * slot generates inside its effective date range. Returns null when the
 * range isn't finite (so the caller can show "Ongoing" instead of a
 * fake number).
 *
 * Mirrors the backend `expand_package_occurrences` weekday math:
 *   Python weekday() is Mon=0..Sun=6  →  JS-style is Sun=0..Sat=6.
 *   We work directly in JS-style here.
 */
export function countSlotSessions(slot, pkg) {
    if (!slot || (slot.type || 'weekly') !== 'weekly') {
        // one_off → exactly one session (unless skipped)
        if (slot && (slot.type === 'one_off')) return 1;
        return null;
    }
    const { start, end } = resolveSlotRange(slot, pkg);
    if (!start || !end) return null;
    const days = Array.isArray(slot.weekdays) && slot.weekdays.length > 0
        ? slot.weekdays
        : (typeof slot.weekday === 'number' ? [slot.weekday] : []);
    if (days.length === 0) return null;
    const daySet = new Set(days);
    // Parse YYYY-MM-DD as a LOCAL date (avoid UTC drift that would shift
    // the weekday at midnight in some zones).
    const parse = (iso) => {
        const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
        return new Date(y, m - 1, d);
    };
    const startD = parse(start);
    const endD = parse(end);
    if (endD < startD) return 0;
    // Build a set of skipped (date string) overrides so we don't count them.
    const skipped = new Set(
        (slot.overrides || [])
            .filter((o) => o && o.skip && o.date)
            .map((o) => o.date.slice(0, 10))
    );
    let count = 0;
    const cursor = new Date(startD);
    while (cursor <= endD) {
        const jsWeekday = cursor.getDay(); // 0=Sun..6=Sat — matches our convention
        if (daySet.has(jsWeekday)) {
            const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
            if (!skipped.has(iso)) count += 1;
        }
        cursor.setDate(cursor.getDate() + 1);
    }
    return count;
}

/**
 * Build a one-line schedule summary for a class slot.
 *
 * Weekly examples:
 *   "Tue & Wed · 4:00 PM · Aug 5 – Aug 26"
 *   "Sat · 10:00 AM"                          (no date range set)
 * One-off example:
 *   "Sat, Aug 16 · 10:00 AM"
 */

/* ------------------------------------------------------------------ */
/* Phase 156 — per-day times on a weekly slot                          */
/*                                                                    */
/* A weekly slot may meet at a DIFFERENT time on each day             */
/* ("Mon 4:00 PM + Wed 5:30 PM" is ONE class a family enrolls in).     */
/* Truth lives in `slot.day_times = [{weekday, time_local}]`; the     */
/* legacy `weekdays` / `weekday` / `time_local` fields are mirrors.   */
/* Every reader should go through `slotDayTimes()` so legacy rows     */
/* (no day_times) keep working.                                       */
/* ------------------------------------------------------------------ */

const _hhmm = (v) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(v || '').trim());
    if (!m) return '';
    const h = Number(m[1]); const mi = Number(m[2]);
    if (h < 0 || h > 23 || mi < 0 || mi > 59) return '';
    return `${String(h).padStart(2, '0')}:${m[2]}`;
};

/** [{weekday, time_local}] sorted by weekday — legacy-safe. */
export function slotDayTimes(slot) {
    if (!slot) return [];
    const out = new Map();
    if (Array.isArray(slot.day_times) && slot.day_times.length) {
        for (const d of slot.day_times) {
            const wd = Number(d?.weekday); const t = _hhmm(d?.time_local);
            if (Number.isInteger(wd) && wd >= 0 && wd <= 6 && t) out.set(wd, t);
        }
    }
    if (out.size === 0) {
        const t = _hhmm(slot.time_local);
        const days = Array.isArray(slot.weekdays) && slot.weekdays.length
            ? slot.weekdays
            : (typeof slot.weekday === 'number' ? [slot.weekday] : []);
        if (t) for (const w of days) { const wd = Number(w); if (Number.isInteger(wd) && wd >= 0 && wd <= 6) out.set(wd, t); }
    }
    return [...out.keys()].sort((a, b) => a - b).map((wd) => ({ weekday: wd, time_local: out.get(wd) }));
}

/** True when the slot meets at more than one distinct time across its days. */
export function slotHasMixedTimes(slot) {
    return new Set(slotDayTimes(slot).map((d) => d.time_local)).size > 1;
}

/** "Mon 4:00 PM & Wed 5:30 PM" (mixed) or "Mon & Wed · 4:00 PM" (same time). */
export function formatSlotDayTimes(slot) {
    const dts = slotDayTimes(slot);
    if (!dts.length) return '';
    if (slotHasMixedTimes(slot)) {
        return dts.map((d) => `${WEEKDAY_SHORT[d.weekday]} ${fmtTime12hLong(d.time_local)}`).join(' & ');
    }
    return `${formatWeekdays(dts.map((d) => d.weekday))} \u00b7 ${fmtTime12hLong(dts[0].time_local)}`;
}

/** Keep the legacy mirror fields in sync with `day_times` on a draft slot. */
export function syncSlotDayTimes(slot) {
    if (!slot || (slot.type || 'weekly') !== 'weekly') return slot;
    const valid = slotDayTimes({ ...slot, weekdays: undefined, weekday: undefined, time_local: undefined });
    const weekdays = valid.map((d) => d.weekday);
    return {
        ...slot,
        weekdays,
        weekday: weekdays[0],
        time_local: valid[0]?.time_local || (Array.isArray(slot.day_times) && slot.day_times[0]?.time_local) || slot.time_local || '',
        mixed_times: new Set(valid.map((d) => d.time_local)).size > 1,
    };
}

export function formatSlotSchedule(slot, pkg) {
    if (!slot) return '';
    const time = fmtTime12hLong(slot.time_local || '');
    if ((slot.type || 'weekly') === 'one_off') {
        const dateStr = formatShortDate(slot.date || '', false);
        const dow = slot.date ? formatWeekdays([new Date(`${slot.date}T12:00:00`).getDay()]) : '';
        const dateLabel = dow && dateStr ? `${dow}, ${dateStr}` : (dateStr || '');
        return [dateLabel, time].filter(Boolean).join(' \u00b7 ');
    }
    const { start, end } = resolveSlotRange(slot, pkg);
    const dateLabel = formatDateRange(start, end);
    // Phase 156 — "Mon 4:00 PM & Wed 5:30 PM" when the days meet at different times.
    if (slotHasMixedTimes(slot)) {
        return [formatSlotDayTimes(slot), dateLabel].filter(Boolean).join(' \u00b7 ');
    }
    const days = slotDayTimes(slot).map((d) => d.weekday);
    const dayLabel = formatWeekdays(days.length ? days : (Array.isArray(slot.weekdays) ? slot.weekdays : (typeof slot.weekday === 'number' ? [slot.weekday] : [])));
    return [dayLabel, time, dateLabel].filter(Boolean).join(' \u00b7 ');
}
